import { useEffect, useMemo, useState } from 'react';
import { api, type ConnectionSummary, type ConnectorManifestSummary } from '../api.ts';
import {
  DUAL_IDENTITY,
  IdentityBadge,
  Tooltip,
  botLabelFor,
  identityTip,
} from '../components/IdentityBadge.tsx';

/**
 * What each connection can do *for you*, what it is allowed to reach, and what
 * it would break to unlink.
 *
 * A connection is a pointer, not a grant — the credential is resolved per call
 * from whoever is acting. That makes "is this set up?" a question with more than
 * one answer, so this screen answers each half separately: your own account, and
 * the workspace bot. A dual-identity connection is routinely half-ready, and the
 * consequence differs — a missing personal link stops steps that run as you, a
 * missing bot credential stops the scheduled ones.
 *
 * Since the connector framework there are two more answers on the same screen,
 * and they are here rather than in an admin corner because they are the same
 * question: **how far does this reach** (the grant) and **is it still the thing
 * that was approved** (the catalog).
 */
export function ConnectionsView({
  connections,
  isAdmin,
  onRefresh,
}: {
  connections: ConnectionSummary[];
  isAdmin: boolean;
  onRefresh(): void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(connections[0]?.id ?? null);
  const [adding, setAdding] = useState(false);

  // Pick up the first load, and stay put once the user has chosen.
  const selected = useMemo(
    () => connections.find((entry) => entry.id === selectedId) ?? connections[0] ?? null,
    [connections, selectedId],
  );

  const needsYou = connections.filter((entry) => entry.usesUser && !entry.userLinked);
  const drifted = connections.filter((entry) => entry.catalogDrifted);

  return (
    <div className="body">
      <aside className="sidebar">
        <p className="section-title">Connections</p>
        {connections.length === 0 && <p className="hint">No connections registered yet.</p>}
        {connections.map((connection) => (
          <button
            key={connection.id}
            className="list-item"
            aria-current={connection.id === selected?.id && !adding}
            onClick={() => {
              setAdding(false);
              setSelectedId(connection.id);
            }}
          >
            {connection.name}
            {/*
              Badges rather than a sentence: the rail is scanned to answer "which
              of these hold two identities, and which half is missing", and two
              marks answer that faster than "you · bot missing" ever could.
            */}
            <small className="row wrap" style={{ gap: '0.25rem', marginTop: '0.15rem' }}>
              {connection.usesUser && (
                <IdentityBadge
                  kind="person"
                  label={connection.userLinked ? 'you' : 'you — not linked'}
                />
              )}
              {connection.usesBot && (
                <IdentityBadge
                  kind="bot"
                  label={
                    connection.botReady
                      ? (connection.serviceAccountLabel ?? 'bot')
                      : `${connection.serviceAccountLabel ?? 'bot'} — missing`
                  }
                />
              )}
              {!connection.usesUser && !connection.usesBot && 'reef internal'}
              {connection.catalogDrifted && <span className="pill warn">catalog changed</span>}
            </small>
          </button>
        ))}

        {isAdmin && (
          <button
            className="list-item"
            aria-current={adding}
            style={{ marginTop: '0.75rem' }}
            onClick={() => setAdding(true)}
          >
            + Add a connection
          </button>
        )}
      </aside>

      <main className="main">
        {needsYou.length > 0 && !adding && (
          <div className="banner warn">
            <h3>
              {needsYou.length} connection{needsYou.length === 1 ? '' : 's'} waiting on you
            </h3>
            <p style={{ margin: '0 0 0.75rem' }}>
              Workflows run against your own access, so steps that act as you will stop here until
              these are linked. Steps that run as a bot are unaffected.
            </p>
            <div className="row wrap">
              {needsYou.map((connection) => (
                <a
                  key={connection.id}
                  className="button primary"
                  href={connection.connectUrl ?? '#'}
                >
                  Connect {connection.name}
                </a>
              ))}
            </div>
          </div>
        )}

        {drifted.length > 0 && !adding && (
          <div className="banner warn">
            <h3>
              {drifted.length} connection{drifted.length === 1 ? '' : 's'} changed since approval
            </h3>
            <p style={{ margin: 0 }}>
              {drifted.map((entry) => entry.name).join(', ')} now offer tools that differ from the
              ones that were approved. Steps keep running against the approved surface; an admin has
              to look at the difference and approve it.
            </p>
          </div>
        )}

        {adding && <AddConnection onDone={() => { setAdding(false); onRefresh(); }} />}

        {!adding && !selected && <div className="empty">No connections registered yet.</div>}

        {!adding && selected && (
          <ConnectionDetail connection={selected} isAdmin={isAdmin} onRefresh={onRefresh} />
        )}
      </main>
    </div>
  );
}

function ConnectionDetail({
  connection,
  isAdmin,
  onRefresh,
}: {
  connection: ConnectionSummary;
  isAdmin: boolean;
  onRefresh(): void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const run = async (label: string, work: () => Promise<string>) => {
    setBusy(label);
    setNote(null);
    try {
      setNote(await work());
      onRefresh();
    } catch (error) {
      setNote((error as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>{connection.name}</h1>
      <p className="hint" style={{ marginTop: 0 }}>
        {DUAL_IDENTITY.has(connection.credentialMode) ? (
          <Tooltip
            text={`This connection holds two credentials. Each step that mounts it chooses which to use — ${identityTip('auto', botLabelFor(connection))}`}
          >
            <span style={{ borderBottom: '1px dotted currentColor' }}>
              {describeMode(connection)}
            </span>
          </Tooltip>
        ) : (
          describeMode(connection)
        )}{' '}
        · <code>{connection.id}</code> · {connection.connector.name}
        {connection.trustTier === 'byo' && <span className="pill warn"> unvetted</span>}
      </p>

      {note && (
        <div className="banner">
          <p style={{ margin: 0 }}>{note}</p>
        </div>
      )}

      <div className="card">
        <h2>Who it runs as</h2>
        <div className="stack">
          {connection.usesUser && (
            <IdentityRow
              kind="person"
              title="Your own account"
              tip={identityTip('person', botLabelFor(connection))}
              ready={connection.userLinked}
              readyText={`Linked. Steps set to run as you will use your own ${connection.name} access and your name will be on the result.`}
              blockedText={`Not linked. Steps that run as you will stop and ask for this${
                connection.usesBot ? '; steps that run as the bot are unaffected' : ''
              }.`}
              action={
                connection.connectUrl ? (
                  <a className="button primary" href={connection.connectUrl}>
                    {connection.userLinked ? 'Reconnect' : 'Connect'} {connection.name}
                  </a>
                ) : null
              }
              /*
                Disconnecting is only offered for a link reef itself holds. A
                better-auth provider is unlinked where better-auth manages it,
                and a button here that quietly did nothing would be worse than
                no button.
              */
              extra={
                connection.canRevoke ? (
                  <button
                    disabled={busy !== null}
                    onClick={() =>
                      run('revoke', async () => {
                        const result = await api.revokeConnection(connection.id);
                        return result.revokedUpstream
                          ? 'Disconnected, and the provider was told to forget the token.'
                          : 'Disconnected here. The provider offers no revocation endpoint, so the token will expire on its own.';
                      })
                    }
                  >
                    Disconnect
                  </button>
                ) : null
              }
            />
          )}

          {connection.usesBot && (
            <IdentityRow
              kind="bot"
              title={connection.serviceAccountLabel ?? 'Workspace bot'}
              tip={identityTip('bot', botLabelFor(connection))}
              ready={connection.botReady}
              readyText="Configured. Scheduled and webhook runs use this, so they keep working when nobody is signed in."
              blockedText={
                connection.secretName
                  ? `Not configured. A workspace admin needs to set ${connection.secretName} — either on the deployment or in Admin → Secrets.`
                  : 'Not configured. A workspace admin needs to set this up.'
              }
              action={null}
            />
          )}

          {!connection.usesUser && !connection.usesBot && (
            <p className="hint" style={{ margin: 0 }}>
              Reef's own credentials. Never handed to a step.
            </p>
          )}
        </div>
      </div>

      <ScopeCard connection={connection} isAdmin={isAdmin} onRefresh={onRefresh} />

      <div className="card">
        <h2>What it reaches</h2>
        <table>
          <tbody>
            <tr>
              <th style={{ width: '11rem' }}>MCP endpoint</th>
              <td>
                <code>{connection.mcpUrl}</code> ({connection.transport})
              </td>
            </tr>
            <tr>
              <th>Network</th>
              <td>
                {connection.egressHosts.length
                  ? connection.egressHosts.map((host) => (
                      <span className="pill" key={host}>
                        {host}
                      </span>
                    ))
                  : 'No extra hosts.'}
              </td>
            </tr>
            <tr>
              <th>Default policy</th>
              <td>
                {connection.defaultPolicyId ? (
                  <span className="pill accent">{connection.defaultPolicyId}</span>
                ) : (
                  'Inherits whatever the step already has.'
                )}
              </td>
            </tr>
            <tr>
              <th>Writes</th>
              <td>
                {connection.stagesWrites
                  ? 'Queued for approval. A step that writes gets a ticket, and the call only happens when a human applies it.'
                  : 'Performed directly. This connector is vetted, so a step may write without a second pair of eyes.'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Tools</h2>
        <p className="hint" style={{ marginTop: 0 }}>
          {connection.toolCount > 0
            ? `${connection.toolCount} tool${connection.toolCount === 1 ? '' : 's'} cached.`
            : 'None cached yet — discovery runs when the connection is authorized.'}
          {connection.catalogFingerprint
            ? ' Approved as they stood when someone last looked at them.'
            : ' Not approved yet.'}
        </p>

        {connection.catalogDrifted && (
          <div className="banner warn">
            <h3>This server's tools have changed</h3>
            <p style={{ margin: 0 }}>
              What it offers no longer matches what was approved. A tool that changed shape may have
              changed what it does, so the change needs looking at rather than adopting.
            </p>
          </div>
        )}

        <div className="row wrap">
          <button
            disabled={busy !== null}
            onClick={() =>
              run('refresh', async () => {
                const result = await api.refreshConnectionTools(connection.id);
                return `${result.tools.length} tools. ${result.driftSummary}${
                  result.needsApproval ? ' An admin needs to approve the new catalog.' : ''
                }`;
              })
            }
          >
            Re-read tools
          </button>

          {isAdmin && (
            <button
              disabled={busy !== null}
              onClick={() =>
                run('approve', async () => {
                  const result = await api.approveCatalog(connection.id);
                  return `Approved ${result.toolCount} tools as they now stand.`;
                })
              }
            >
              Approve this catalog
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <h2>Used by</h2>
        {connection.usedBy.length === 0 ? (
          <p className="hint" style={{ margin: 0 }}>
            No workflow you can see mounts this connection.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Workflow</th>
                <th>Runs as</th>
              </tr>
            </thead>
            <tbody>
              {connection.usedBy.map((workflow) => (
                <tr key={workflow.id}>
                  <td>{workflow.name}</td>
                  <td>
                    <span className="row wrap">
                      {workflow.actors.map((actor) => {
                        const kind =
                          actor === 'user' ? 'person' : actor === 'service' ? 'bot' : 'auto';
                        return (
                          <IdentityBadge
                            key={actor}
                            kind={kind}
                            label={actorLabel(actor, connection)}
                            tip={identityTip(kind, botLabelFor(connection))}
                          />
                        );
                      })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="row">
        <button onClick={onRefresh}>Re-check</button>
      </div>
    </>
  );
}

/**
 * The grant, editable in place.
 *
 * Shown to everyone and editable by admins, because "what can this reach" is a
 * question every step author has and a decision only an admin should make.
 */
function ScopeCard({
  connection,
  isAdmin,
  onRefresh,
}: {
  connection: ConnectionSummary;
  isAdmin: boolean;
  onRefresh(): void;
}) {
  const saved = connection.scope.join('\n');
  const [draft, setDraft] = useState(saved);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /*
   * A different connection selected is a different grant; forget the draft.
   *
   * Keyed on the grant's *text* rather than on `connection.scope`, which is a
   * fresh array on every list refresh even when the grant has not moved. Keyed
   * on the array, any refresh landing while somebody was mid-edit — the shell
   * re-fetches connections on mount and after every write — silently threw the
   * edit away and re-disabled Save, so the box claimed to be showing what the
   * server held while the typing had gone.
   */
  useEffect(() => {
    setDraft(saved);
    setError(null);
  }, [connection.id, saved]);

  const dirty = draft !== saved;

  return (
    <div className="card">
      <h2>Grant</h2>
      <p className="hint" style={{ marginTop: 0 }}>
        Resource URLs this connection may reach, one per line. <code>*</code> matches one segment
        and <code>**</code> matches the rest, so <code>{connection.connector.scopeScheme}://prod/**</code>{' '}
        is prod and everything under it. A call outside the grant is refused before it runs.
        {connection.scope.length === 0 &&
          ' No grant is set, so nothing is narrowed — every resource this server exposes is reachable.'}
      </p>

      {isAdmin ? (
        <>
          <textarea
            rows={Math.max(3, connection.scope.length + 1)}
            value={draft}
            spellCheck={false}
            style={{ width: '100%', fontFamily: 'ui-monospace, monospace' }}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={`${connection.connector.scopeScheme}://service/**`}
          />
          {error && <p className="hint">{error}</p>}
          <div className="row">
            <button
              disabled={!dirty || saving}
              onClick={async () => {
                setSaving(true);
                setError(null);
                try {
                  await api.updateConnectionScope(
                    connection.id,
                    draft.split('\n').map((line) => line.trim()).filter(Boolean),
                  );
                  onRefresh();
                } catch (saveError) {
                  setError((saveError as Error).message);
                } finally {
                  setSaving(false);
                }
              }}
            >
              Save grant
            </button>
            {dirty && (
              <button disabled={saving} onClick={() => setDraft(saved)}>
                Discard
              </button>
            )}
          </div>
        </>
      ) : connection.scope.length ? (
        <div className="row wrap">
          {connection.scope.map((grant) => (
            <span className="pill accent" key={grant}>
              {grant}
            </span>
          ))}
        </div>
      ) : (
        <p className="hint" style={{ margin: 0 }}>
          Unscoped.
        </p>
      )}
    </div>
  );
}

/**
 * Register a connection from a manifest.
 *
 * The form is the manifest's — reef renders whatever fields the connector
 * declares, and knows nothing about what they mean. That is the whole claim of
 * the registry: adding ClickHouse was a manifest, not a screen.
 */
function AddConnection({ onDone }: { onDone(): void }) {
  const [connectors, setConnectors] = useState<ConnectorManifestSummary[]>([]);
  const [provider, setProvider] = useState<string>('');
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [scope, setScope] = useState('');
  // Once somebody edits the grant it is theirs. Without this the suggestion
  // effect below reruns on every configurator keystroke and overwrites what
  // they typed, which is maddening in exactly the case the field matters.
  const [scopeEdited, setScopeEdited] = useState(false);
  const [issues, setIssues] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ message: string; connectUrl: string | null } | null>(null);

  useEffect(() => {
    api.listConnectors().then((response) => setConnectors(response.connectors)).catch(() => {});
  }, []);

  const manifest = connectors.find((entry) => entry.provider === provider) ?? null;

  // Suggested grants are pre-filled from the configurator as it is typed, so the
  // common case is a form you fill in once rather than a grammar you learn.
  useEffect(() => {
    if (!manifest || scopeEdited) return;
    setName((current) => current || manifest.name);
    setScope(
      manifest.suggestedScopes
        .map((suggestion) =>
          suggestion.replace(/\{([a-zA-Z0-9_]+)\}/g, (whole, key: string) => values[key] || whole),
        )
        .join('\n'),
    );
  }, [manifest, values, scopeEdited]);

  if (result) {
    return (
      <div className="card">
        <h2>Registered</h2>
        <p>{result.message}</p>
        {result.connectUrl && (
          <p>
            <a className="button primary" href={result.connectUrl}>
              Connect it now
            </a>
          </p>
        )}
        <div className="row">
          <button onClick={onDone}>Back to connections</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>Add a connection</h2>

      <div className="field">
        <label htmlFor="connector">Connector</label>
        <select
          id="connector"
          value={provider}
          onChange={(event) => {
            setProvider(event.target.value);
            setValues({});
            setScopeEdited(false);
            setIssues([]);
          }}
        >
          <option value="">Choose…</option>
          {connectors.map((entry) => (
            <option key={entry.provider} value={entry.provider}>
              {entry.name}
            </option>
          ))}
        </select>
        {manifest && <span className="hint">{manifest.summary}</span>}
      </div>

      {manifest && (
        <>
          <div className="field">
            <label htmlFor="connection-id">Id</label>
            <input
              id="connection-id"
              value={id}
              placeholder={`${manifest.provider}-prod`}
              onChange={(event) => setId(event.target.value)}
            />
            <span className="hint">
              What workflow documents mount. Short, stable, and awkward to change later.
            </span>
          </div>

          <div className="field">
            <label htmlFor="connection-name">Name</label>
            <input
              id="connection-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          {manifest.configurator.map((field) => (
            <div className="field" key={field.key}>
              <label htmlFor={`cfg-${field.key}`}>
                {field.label}
                {field.required ? ' *' : ''}
              </label>
              {field.options ? (
                <select
                  id={`cfg-${field.key}`}
                  value={values[field.key] ?? ''}
                  onChange={(event) =>
                    setValues({ ...values, [field.key]: event.target.value })
                  }
                >
                  <option value="">Choose…</option>
                  {field.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={`cfg-${field.key}`}
                  value={values[field.key] ?? ''}
                  placeholder={field.placeholder}
                  onChange={(event) =>
                    setValues({ ...values, [field.key]: event.target.value })
                  }
                />
              )}
              {field.help && <span className="hint">{field.help}</span>}
            </div>
          ))}

          <div className="field">
            <label htmlFor="connection-scope">
              Grant{manifest.scopeRequired ? ' *' : ''}
            </label>
            <textarea
              id="connection-scope"
              rows={3}
              value={scope}
              spellCheck={false}
              style={{ width: '100%', fontFamily: 'ui-monospace, monospace' }}
              onChange={(event) => {
                setScopeEdited(true);
                setScope(event.target.value);
              }}
              placeholder={`${manifest.scopeScheme}://service/**`}
            />
            <span className="hint">
              One resource URL per line.
              {manifest.scopeRequired
                ? ' This connector will not register without one.'
                : ' Leave empty to reach everything this server exposes.'}
            </span>
          </div>

          {manifest.stagesWrites && (
            <p className="hint">
              {manifest.trustTier === 'byo'
                ? 'Nothing has reviewed what is on the other end of this connector, so every write through it will be queued for a human rather than performed.'
                : 'Writes through this connector are queued for a human rather than performed. Reef will reach it; it will not reshape it unattended.'}
            </p>
          )}

          {issues.length > 0 && (
            <div className="banner danger">
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                {issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="row">
            <button
              className="primary"
              disabled={saving || !id.trim()}
              onClick={async () => {
                setSaving(true);
                setIssues([]);
                try {
                  const response = await api.createConnection({
                    id: id.trim(),
                    name: name.trim() || manifest.name,
                    provider: manifest.provider,
                    configurator: values,
                    scope: scope.split('\n').map((line) => line.trim()).filter(Boolean),
                  });
                  setResult({
                    message:
                      response.toolsDiscovered > 0
                        ? `${response.connection.name} is registered and ${response.toolsDiscovered} tools were discovered.`
                        : response.discoveryError
                          ? `${response.connection.name} is registered, but discovering its tools failed: ${response.discoveryError}`
                          : (response.note ?? `${response.connection.name} is registered.`),
                    connectUrl: response.connectUrl ?? null,
                  });
                } catch (error) {
                  setIssues([(error as Error).message]);
                } finally {
                  setSaving(false);
                }
              }}
            >
              Register
            </button>
            <button disabled={saving} onClick={onDone}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function IdentityRow({
  kind,
  title,
  tip,
  ready,
  readyText,
  blockedText,
  action,
  extra,
}: {
  kind: 'person' | 'bot';
  title: string;
  tip: string;
  ready: boolean;
  readyText: string;
  blockedText: string;
  action: React.ReactNode;
  extra?: React.ReactNode;
}) {
  return (
    <div className="field">
      <div className="row">
        {/*
          Two separate signals, and they answer different questions. The badge
          says *who* this is — a person or a shared machine account — and never
          changes. The pill says whether it currently works, which does.
        */}
        <IdentityBadge kind={kind} label={title} tip={tip} size="lg" />
        <span className={ready ? 'pill ok' : 'pill warn'}>{ready ? 'Ready' : 'Not set up'}</span>
      </div>
      <span className="hint">{ready ? readyText : blockedText}</span>
      {(action || extra) && (
        <div className="row wrap">
          {action}
          {extra}
        </div>
      )}
    </div>
  );
}

function describeMode(connection: ConnectionSummary): string {
  switch (connection.credentialMode) {
    case 'user_oauth':
      return 'Always runs as the person using it';
    case 'service':
      return 'Always runs as the workspace bot';
    case 'user_or_service':
    case 'github_app':
      return 'Runs as you or as the bot — each step decides';
    case 'system':
      return "Reef's own credentials, never given to a step";
    default:
      return connection.credentialMode;
  }
}

function actorLabel(actor: string, connection: ConnectionSummary): string {
  const bot = connection.serviceAccountLabel ?? 'bot';
  if (actor === 'user') return 'you';
  if (actor === 'service') return bot;
  return connection.usesUser && connection.usesBot ? `you, or ${bot} unattended` : 'default';
}
